import React, { useEffect, useState, useCallback, useRef } from "react";
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { map } from "lodash";
import { useDispatch } from "react-redux";
import ApiEngine from '../../util/ApiEngine';
import { ApiUrl, ApiKey, SessionKey } from "../../util/Constant";
import { useTranslation } from "react-i18next";

const LatestWinSlider = () => {
    const [latestWinData, setLatestWinData] = useState([]);
    const sliderRef = useRef(null);
    const { t, i18n } = useTranslation();
    const [selectedCurrency, setSelectedCurrency] = useState("ALL"); // ALL, MYR, THB, AUD, IDR

    // Map display labels to currency codes
    const currencyMap = {
        "ALL": "ALL",
        "MY": "MYR",
        "TH": "THB",
        "AU": "AUD",
        "ID": "IDR"
    };

    // Reverse map for getting label from currency
    const labelMap = {
        "ALL": "ALL",
        "MYR": "MY",
        "THB": "TH",
        "AUD": "AU",
        "IDR": "ID"
    };

    const lowerCaseAndDeleteLastChar = useCallback((str) => {
        return str.toLowerCase().slice(0, -1);
    }, []);

    const getCurrencySymbol = useCallback((country) => {
        switch (country) {
            case "MYR": return "RM ";
            case "IDR": return "RP ";
            case "AUD": return "$ ";
            default: return "฿ ";
        }
    }, []);

    // Handle tab click - convert label to currency code
    const handleTabClick = (label) => {
        const currency = currencyMap[label];
        setSelectedCurrency(currency);
    };

    // Fetch latest win data directly in the component
    const fetchLatestWinData = async () => {
        try {
            let responseJson = await ApiEngine.get(ApiUrl._API_GET_LATEST_WIN + "?currency=" + selectedCurrency);

            if (responseJson[ApiKey._API_SUCCESS_KEY]) {
                const originalData = responseJson[ApiKey._API_DATA_KEY];
                setLatestWinData(originalData);
            } else {
                setLatestWinData([]);
            }

        } catch {
            setLatestWinData([]);
        }
    };

    // Fetch data when selected currency changes
    useEffect(() => {
        fetchLatestWinData();
    }, [selectedCurrency]);

    // Animation effect
    useEffect(() => {
        if (latestWinData.length > 0 && sliderRef.current) {
            const slider = sliderRef.current;
            if (!slider || latestWinData.length === 0) return;
            let animationId;
            let lastTimestamp = 0;
            const scrollSpeed = 0.082; // Pixels per millisecond

            const animate = (timestamp) => {
                if (!lastTimestamp) lastTimestamp = timestamp;
                const deltaTime = timestamp - lastTimestamp;

                if (slider.scrollLeft >= slider.scrollWidth / 2) {
                    slider.scrollLeft = 0;
                } else {
                    slider.scrollLeft += scrollSpeed * deltaTime;
                }

                lastTimestamp = timestamp;
                animationId = requestAnimationFrame(animate);
            };

            animationId = requestAnimationFrame(animate);

            return () => cancelAnimationFrame(animationId);
        }
    }, [latestWinData]);

    function numberWithCommas(x) {
        return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }

    return (
        <>
            {latestWinData.length > 0 && (
                <>
                    <div className="latest-payout-wrapper mb-3">
                        <div className="latest-payout-title">
                            <b></b> <h2 className="mb-0 ml-4">{t("LATEST_PAYOUT")}</h2>
                        </div>
                    </div>

                    <div id="latest-win-filter" className="tab-container">
                        <div
                            className={`tab-item ${selectedCurrency === "ALL" ? "active" : ""}`}
                            onClick={() => handleTabClick("ALL")}
                        >
                            ALL
                        </div>
                        <div
                            className={`tab-item ${selectedCurrency === "MYR" ? "active" : ""}`}
                            onClick={() => handleTabClick("MY")}
                        >
                            MY
                        </div>
                        <div
                            className={`tab-item ${selectedCurrency === "THB" ? "active" : ""}`}
                            onClick={() => handleTabClick("TH")}
                        >
                            TH
                        </div>
                        <div
                            className={`tab-item ${selectedCurrency === "AUD" ? "active" : ""}`}
                            onClick={() => handleTabClick("AU")}
                        >
                            AU
                        </div>
                        <div
                            className={`tab-item ${selectedCurrency === "IDR" ? "active" : ""}`}
                            onClick={() => handleTabClick("ID")}
                        >
                            ID
                        </div>
                    </div>

                    <div className="container latest-win-section">
                        <div className="latest-win-border-bgColor">
                            <div className="marquee-container" ref={sliderRef}>
                                <div className="latest-win-container">
                                    {latestWinData.length > 0 &&
                                        map([...latestWinData, ...latestWinData], (item, index) => (
                                            <div key={index} className="latest-win-content">
                                                <div>
                                                    <div className="latest-win-image">
                                                        <LazyLoadImage
                                                            src={encodeURI(item.game_image)}
                                                            className="img-100"
                                                            alt="banner"
                                                        />
                                                    </div>
                                                    <div className="latest-win-data">
                                                        <div className="latest-win-username">
                                                            <img
                                                                className="mr-2"
                                                                src={encodeURI(`https://cdn.i8global.com/i8app/${lowerCaseAndDeleteLastChar(item.bet_country)}-circle.png`)}
                                                                alt={item.bet_country}
                                                            />
                                                            {(item.bet_username)}
                                                        </div>
                                                        <div className="latest-win-amount">
                                                            {getCurrencySymbol(item.bet_country)}
                                                            {numberWithCommas(parseFloat(item.bet_win).toFixed(2))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default LatestWinSlider;